// hooks/usePosts.ts — reactive hook for the post feed.
//
// Reads the post list from postStore reactively (re-renders on every change).
// Exposes service mutations so components only need this hook.
// Components must NOT import postStore or postsService directly.

import { usePostStore } from '@/core/stores/postStore';
import { postsService } from '@/core/services/postsService';
import type { Post, PostMedia, PollDuration } from '@/core/models/post';

export interface CreatePostInput {
  attachments?: import('@/features/home/composer/studio/contracts').AuthoredAttachments;
  question?: boolean;
  audienceRing?: { id: string; revision: number };
  perspectiveSplit?: { slug: string; revision: number };
  collaborativeDraft?: { slug: string; revision: number; version: number };
  body: string;
  media?: PostMedia;
  mediaKey?: string;
  /** Uploaded images in display order (postsService already forwards these). */
  images?: { key: string; alt?: string; width?: number; height?: number }[];
  /** 1.27.60 Clip — see postsService.createPost. */
  clipOf?: string;
  clipStartMs?: number;
  clipEndMs?: number;
  /** Video posts — see postsService.createPost for what each one means. */
  videoId?: string;
  videoTitle?: string;
  videoCategory?: import('@/core/models/post').VideoCategoryId | '';
  videoShort?: boolean;
  videoAllowDownload?: boolean;
  videoAllowEmbed?: boolean;
  videoAllowClips?: boolean;
  audience?: import('@/core/models/post').AudienceType;
  regions?: string[];
  people?: string[];
  poll?: { options: string[]; duration: PollDuration };
  scheduledAt?: string;
  draft?: boolean;
  /** Quote posts (1.21.00 Slice 2): id or share code of the quoted post. */
  quotedPostId?: string;
  /** Threads (1.24.00 C2): id or share code of the post this one continues. */
  threadRootId?: string;
}

export interface PostsHook {
  /** All posts in feed order. */
  posts: Post[];
  /** Toggle the viewer's Cherish, the one appreciation reaction (it replaced Like). */
  toggleCheer: (id: string) => Promise<Post | undefined>;
  /** Toggle Spread-It!: put the post in followers' feeds, or take it back. */
  spread: (id: string) => Promise<Post | undefined>;
  /** Create a new post as the current user; prepends to the feed. */
  createPost: (input: CreatePostInput) => Promise<Post>;
  /** Cast/replace the viewer's poll vote on a post. */
  pollVote: (postId: string, optionId: string) => Promise<Post>;
}

export function usePosts(): PostsHook {
  const posts = usePostStore((s) => s.posts);
  return {
    posts,
    toggleCheer: (id) => postsService.toggleCheer(id),
    spread: (id) => postsService.spread(id),
    createPost: (input) => postsService.createPost(input),
    pollVote: (postId, optionId) => postsService.pollVote(postId, optionId),
  };
}
